import { useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CriticalIcon,
  EmptyState,
  Flex,
  FolderIcon,
  Heading,
  PlusIcon,
  Skeleton,
  Text,
  List,
  VisuallyHidden,
  useIsMobileScreenSize,
  CheckIcon,
} from '@buffer-mono/popcorn'

import { ChannelGroupDialog } from '~publish/components/ChannelGroupDialog'
import {
  useAccountId,
  useIsFreeUser,
  useIsOneBufferOrg,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { useCta } from '~publish/hooks/useCta'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { navigateToBilling } from '~publish/helpers/navigateToBilling'

import ChannelGroupItem from './components/ChannelGroupItem'
import { ChannelGroupsUpgradePathIllustration } from './components/ChannelGroupsUpgradePathIllustration'
import { DeleteConfirmationDialog } from './components/DeleteConfirmationDialog'
import type { ChannelGroup_ChannelGroup } from './hooks/useChannelGroups'
import { useChannelGroups } from './hooks/useChannelGroups'
import { NewSettingsPageLayout } from '../shared/components/NewSettingsPageLayout'

import styles from './ChannelGroups.module.css'

export const ChannelGroups = (): JSX.Element => {
  const isMobile = useIsMobileScreenSize()
  const accountId = useAccountId()
  const organizationId = useOrganizationId()
  const isOneBufferOrg = useIsOneBufferOrg()
  const isFreeUser = useIsFreeUser()

  const ctaKey = 'publish-settings-channelGroups-upgrade-1'
  const upgradePathName = 'channelGroups-upgrade'
  const cta = useCta(ctaKey, {
    upgradePathName,
  })
  const openGlobalModal = useOpenGlobalModal()

  const handleClick = (): void => {
    cta.clicked()
    if (!isOneBufferOrg) {
      navigateToBilling(ctaKey)
    } else {
      openGlobalModal(MODALS.planSelector, {
        cta: ctaKey,
        upgradePathName,
      })
    }
  }

  // State for managing the edit/create dialog
  const [editingChannelGroup, setEditingChannelGroup] = useState<
    ChannelGroup_ChannelGroup | undefined
  >(undefined)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  // State for managing the delete confirmation dialog
  const [deletingChannelGroup, setDeletingChannelGroup] = useState<
    ChannelGroup_ChannelGroup | undefined
  >(undefined)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const {
    channelGroups,
    loading: loadingChannelGroups,
    error: errorLoadingChannelGroups,
  } = useChannelGroups({
    accountId: accountId ?? '',
    organizationId: organizationId ?? '',
  })

  const handleEditChannelGroup = (
    channelGroup: ChannelGroup_ChannelGroup,
  ): void => {
    setEditingChannelGroup(channelGroup)
    setIsDialogOpen(true)
  }

  const handleCreateChannelGroup = (): void => {
    setIsDialogOpen(true)
  }

  const handleCloseDialog = (): void => {
    setIsDialogOpen(false)
    setEditingChannelGroup(undefined)
  }

  const handleDeleteChannelGroup = (
    channelGroup: ChannelGroup_ChannelGroup,
  ): void => {
    setDeletingChannelGroup(channelGroup)
    setIsDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = (): void => {
    setIsDeleteDialogOpen(false)
    setDeletingChannelGroup(undefined)
  }

  const newChannelGroupButton = (
    <Button onClick={handleCreateChannelGroup}>
      {!isMobile && <PlusIcon />}
      {isMobile ? 'New Group' : 'New Channel Group'}
    </Button>
  )

  // Upgrade path
  const bulletPointsOneBuffer = [
    'Schedule up to 2000 posts at a time',
    'Advanced publishing, planning and engaging features',
    'In-depth social analytics and reposting',
  ]
  const bulletPointsLegacy = [
    "No charge for channels you don't use",
    'Access to Analytics and Engage included on all paid plans',
    'Access to all new features guaranteed',
    'Access to Ideas, Templates and Feeds',
    'Access to our beta program',
  ]
  const showUpgradePath = isFreeUser
  const upgradePathTitle = isOneBufferOrg
    ? 'Upgrade to Create Channel Groups'
    : 'Unlock Channel Groups'
  const upgradePathDescription = isOneBufferOrg ? (
    'Group your channels based on how you plan your social content to make planning faster than ever. Get all this, plus:'
  ) : (
    <p>
      You&apos;re on a <strong>legacy Buffer plan</strong>. Switch over to the
      newest version of Buffer to unlock Channel Groups. With channel groups,
      you can group your channels based on how you plan your social content to
      make planning faster than ever. Get all this, plus:
    </p>
  )
  const upgradePathButton = isOneBufferOrg ? (
    <Button variant="primary" size="large" onClick={handleClick}>
      See Upgrade Options
    </Button>
  ) : (
    <Button variant="primary" size="large" onClick={handleClick}>
      Change Plan
    </Button>
  )
  const bulletPoints = isOneBufferOrg
    ? bulletPointsOneBuffer
    : bulletPointsLegacy
  const upgradePathView = (
    <div className={styles.upgradePathContainer}>
      <Flex
        direction="row"
        className={styles.upgradePath}
        justify="between"
        align="center"
        gap="xl"
      >
        <Flex direction="column" gap="sm">
          <Heading as="h1" size="large">
            {upgradePathTitle}
          </Heading>
          <Text>{upgradePathDescription}</Text>
          <List>
            {bulletPoints.map((bulletPoint) => (
              <List.Item key={bulletPoint} icon={CheckIcon}>
                <Text>{bulletPoint}</Text>
              </List.Item>
            ))}
          </List>
          {upgradePathButton}
        </Flex>
        <Flex
          direction="column"
          justify="center"
          className={styles.imageWrapper}
        >
          <ChannelGroupsUpgradePathIllustration />
        </Flex>
      </Flex>
    </div>
  )

  // Skeleton loading view
  const skeletonLoading =
    (!accountId || !organizationId || loadingChannelGroups) && isOneBufferOrg
  const skeletonLoadingView = [0, 1, 2].map((index) => (
    <Skeleton key={index} width="100%" height="74px" />
  ))

  // Error loading view
  const errorLoadingView = (
    <EmptyState variant="critical">
      <EmptyState.Icon>
        <CriticalIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Failed to load channel groups</EmptyState.Heading>
      <EmptyState.Description>
        Something went wrong. Please try again later.
      </EmptyState.Description>
    </EmptyState>
  )

  // Channel groups empty view
  const showChannelGroupsEmpty =
    !showUpgradePath &&
    !loadingChannelGroups &&
    !errorLoadingChannelGroups &&
    channelGroups &&
    channelGroups.length === 0
  const channelGroupsEmptyView = (
    <EmptyState variant="neutral" size="xlarge" className={styles.emptyState}>
      <EmptyState.Icon>
        <FolderIcon />
      </EmptyState.Icon>
      <VisuallyHidden>
        <EmptyState.Heading>No channel groups</EmptyState.Heading>
      </VisuallyHidden>
      <EmptyState.Description className={styles.emptyStateDescription}>
        Group your frequently used channels together to save time when creating
        content.
      </EmptyState.Description>
      <EmptyState.Actions>
        <Button onClick={handleCreateChannelGroup}>
          {!isMobile && <PlusIcon />}
          {isMobile ? 'New Group' : 'New Channel Group'}
        </Button>
      </EmptyState.Actions>
    </EmptyState>
  )

  // Channel groups view
  const showChannelGroups =
    !showUpgradePath &&
    !loadingChannelGroups &&
    !errorLoadingChannelGroups &&
    channelGroups &&
    channelGroups.length > 0

  const channelGroupsView =
    channelGroups &&
    channelGroups.map((channelGroup) => (
      <ChannelGroupItem
        key={String(channelGroup.id)}
        channelGroup={channelGroup}
        onEdit={handleEditChannelGroup}
        onDelete={handleDeleteChannelGroup}
      />
    ))

  return (
    <>
      {showUpgradePath && upgradePathView}
      {!showUpgradePath && (
        <>
          <NewSettingsPageLayout
            title="Channel Groups"
            subtitle="Channel groups are only visible on your account."
            actions={showChannelGroups ? newChannelGroupButton : null}
            inlineActionsOnMobile
          >
            {skeletonLoading && skeletonLoadingView}
            {errorLoadingChannelGroups && errorLoadingView}
            {showChannelGroups && channelGroupsView}
            {showChannelGroupsEmpty && channelGroupsEmptyView}
          </NewSettingsPageLayout>
          {/* Create / Edit Group dialog */}
          <ChannelGroupDialog
            channelGroup={editingChannelGroup}
            isOpen={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            onClose={handleCloseDialog}
            isMobile={isMobile}
          />
          {/* Delete Group dialog */}
          {deletingChannelGroup && (
            <DeleteConfirmationDialog
              channelGroup={deletingChannelGroup}
              open={isDeleteDialogOpen}
              onOpenChange={setIsDeleteDialogOpen}
              onDeleteSuccess={handleCloseDeleteDialog}
            />
          )}
        </>
      )}
    </>
  )
}

export default ChannelGroups
